import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Phone, MessageCircle, ShieldCheck } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { PHONE_DISPLAY, PHONE_TEL, RIT_KEYS, WA_LINK, waLink } from "../../lib/site";
import { useLang } from "../../lib/i18n";

const INITIAL = {
    naam: "",
    telefoon: "",
    ophaaladres: "",
    bestemming: "",
    datum: "",
    tijd: "",
    personen: "1",
    soortRit: "regular",
    opmerkingen: "",
};

const Label = ({ children, htmlFor, required }) => (
    <label htmlFor={htmlFor} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
        {children} {required && <span className="text-amber-400">*</span>}
    </label>
);

export const Booking = () => {
    const { t } = useLang();
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState(INITIAL);

    useEffect(() => {
        const rit = searchParams.get("rit");
        if (rit && RIT_KEYS.includes(rit)) {
            setForm((prev) => ({ ...prev, soortRit: rit }));
        }
    }, [searchParams]);

    const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const submit = (e) => {
        e.preventDefault();
        const msg = t.booking.message(form, t.booking.ritTypes[form.soortRit]);
        window.open(waLink(msg), "_blank", "noopener,noreferrer");
    };

    return (
        <section id="boeken" data-testid="booking-section" className="relative py-24 sm:py-32">
            <div className="container-x grid gap-14 lg:grid-cols-5 lg:gap-20">
                <div className="lg:col-span-2">
                    <SectionHeading
                        num="07"
                        eyebrow={t.booking.eyebrow}
                        title={t.booking.title}
                        text={t.booking.text}
                    />
                    <Reveal delay={0.25}>
                        <div className="mt-10 space-y-4">
                            <a href={PHONE_TEL} data-testid="booking-call-alt" className="card-lift flex items-center gap-4 rounded-2xl border border-white/8 bg-night-850/80 p-5">
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-400/10 text-amber-400">
                                    <Phone size={20} />
                                </span>
                                <span>
                                    <span className="block text-xs text-slate-500">{t.booking.callAltLabel}</span>
                                    <span className="font-mono text-lg font-bold tracking-wider text-white">{PHONE_DISPLAY}</span>
                                </span>
                            </a>
                            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="booking-whatsapp-alt" className="card-lift flex items-center gap-4 rounded-2xl border border-white/8 bg-night-850/80 p-5">
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-400/10 text-amber-400">
                                    <MessageCircle size={20} />
                                </span>
                                <span>
                                    <span className="block text-xs text-slate-500">{t.booking.waAltLabel}</span>
                                    <span className="text-sm font-semibold text-white">{t.booking.waAltTitle}</span>
                                </span>
                            </a>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.15} className="lg:col-span-3">
                    <form
                        onSubmit={submit}
                        data-testid="booking-form"
                        className="rounded-3xl border border-white/10 bg-night-850/90 p-6 shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9)] sm:p-10"
                    >
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <Label htmlFor="bk-naam" required>{t.booking.labels.naam}</Label>
                                <input id="bk-naam" data-testid="booking-input-naam" required value={form.naam} onChange={set("naam")} className="field" placeholder={t.booking.placeholders.naam} autoComplete="name" />
                            </div>
                            <div>
                                <Label htmlFor="bk-telefoon" required>{t.booking.labels.telefoon}</Label>
                                <input id="bk-telefoon" data-testid="booking-input-telefoon" required type="tel" value={form.telefoon} onChange={set("telefoon")} className="field" placeholder={t.booking.placeholders.telefoon} autoComplete="tel" />
                            </div>
                            <div>
                                <Label htmlFor="bk-ophaal" required>{t.booking.labels.ophaaladres}</Label>
                                <input id="bk-ophaal" data-testid="booking-input-ophaaladres" required value={form.ophaaladres} onChange={set("ophaaladres")} className="field" placeholder={t.booking.placeholders.ophaaladres} />
                            </div>
                            <div>
                                <Label htmlFor="bk-bestemming" required>{t.booking.labels.bestemming}</Label>
                                <input id="bk-bestemming" data-testid="booking-input-bestemming" required value={form.bestemming} onChange={set("bestemming")} className="field" placeholder={t.booking.placeholders.bestemming} />
                            </div>
                            <div>
                                <Label htmlFor="bk-datum" required>{t.booking.labels.datum}</Label>
                                <input id="bk-datum" data-testid="booking-input-datum" required type="date" value={form.datum} onChange={set("datum")} className="field [color-scheme:dark]" />
                            </div>
                            <div>
                                <Label htmlFor="bk-tijd" required>{t.booking.labels.tijd}</Label>
                                <input id="bk-tijd" data-testid="booking-input-tijd" required type="time" value={form.tijd} onChange={set("tijd")} className="field [color-scheme:dark]" />
                            </div>
                            <div>
                                <Label htmlFor="bk-personen" required>{t.booking.labels.personen}</Label>
                                <input id="bk-personen" data-testid="booking-input-personen" required type="number" min="1" max="8" value={form.personen} onChange={set("personen")} className="field" />
                            </div>
                            <div>
                                <Label htmlFor="bk-soort">{t.booking.labels.soort}</Label>
                                <select id="bk-soort" data-testid="booking-input-soort" value={form.soortRit} onChange={set("soortRit")} className="field appearance-none">
                                    {RIT_KEYS.map((key) => (
                                        <option key={key} value={key} className="bg-night-900">{t.booking.ritTypes[key]}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <Label htmlFor="bk-opmerkingen">{t.booking.labels.opmerkingen}</Label>
                                <textarea id="bk-opmerkingen" data-testid="booking-input-opmerkingen" rows="3" value={form.opmerkingen} onChange={set("opmerkingen")} className="field resize-none" placeholder={t.booking.placeholders.opmerkingen} />
                            </div>
                        </div>
                        <button type="submit" data-testid="booking-submit" className="btn-amber mt-8 w-full px-8 py-4 text-base sm:text-lg">
                            <Send size={18} /> {t.booking.submit}
                        </button>
                        <p className="mt-5 flex items-center justify-center gap-2 text-center text-xs text-slate-500 sm:text-sm" data-testid="booking-note">
                            <ShieldCheck size={15} className="text-amber-400" />
                            {t.booking.note}
                        </p>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
